import { Box, Button, FormControl, FormLabel, Input, Text, Link } from '@chakra-ui/react';
import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { Redirect, useLocation } from 'react-router-dom';

import PageContainer from '../../components/pageContainer/PageContainer';
import { handleSignUp, handleVerifyEmail, handleResendCode } from '../../slice/signUpSlice';
import { isValidEmail, getPasswordValidation } from '../../utils/validation';

function Signup() {
  const dispatch = useDispatch();
  const location = useLocation();
  const [isFormDisabled, setIsFormDisabled] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [code, setCode] = useState('');
  const [localError, setLocalError] = useState('');
  const [redirect, setRedirect] = useState(false);
  const [isVerificationStep, setIsVerificationStep] = useState(false);
  const [resendDisabled, setResendDisabled] = useState(false);

  // Check if we're coming from login with verification required
  useEffect(() => {
    if (location.state?.isVerificationStep && location.state?.email) {
      setIsVerificationStep(true);
      setEmail(location.state.email);
    }
  }, [location]);

  const handleSubmit = async e => {
    e.preventDefault();
    
    if (!isVerificationStep) {
      // Step 1: Signup
      if (password !== confirmPassword) {
        setLocalError('Passwords do not match');
        return;
      }

      const { isValid, issues } = getPasswordValidation(password);
      if (!isValid) {
        setLocalError(issues[0] || 'Password is invalid');
        return;
      }

      setLocalError('');
      setIsFormDisabled(true);

      const res = (await dispatch(handleSignUp({ email, password, confirmPassword }))).payload;
      if (res.ok) {
        setIsFormDisabled(false);
        setIsVerificationStep(true);
      } else {
        setIsFormDisabled(false);
        setLocalError(res.body?.errmsg || 'Failed to create account');
      }
    } else {
      // Step 2: Verify
      setLocalError('');
      setIsFormDisabled(true);
      
      const res = (await dispatch(handleVerifyEmail({ email, code }))).payload;
      if (res.ok) {
        setRedirect(true);
      } else {
        setIsFormDisabled(false);
        setLocalError(res.body?.errmsg || 'Verification failed');
      }
    }
  };

  const handleResend = async () => {
    setLocalError('');
    setResendDisabled(true);
    
    const res = (await dispatch(handleResendCode({ email }))).payload;
    if (!res.ok) {
      setLocalError(res.body?.errmsg || 'Failed to resend code');
    }
    
    // Re-enable after 60 seconds
    setTimeout(() => setResendDisabled(false), 60000);
  };

  const isEmailEmpty = email.length === 0;
  const isPasswordEmpty = password.length === 0;
  const isConfirmEmpty = confirmPassword.length === 0;

  const passwordValidation = getPasswordValidation(password);
  const isPasswordValid = passwordValidation.isValid;

  const isButtonDisabled =
    !isValidEmail(email) ||
    !isPasswordValid ||
    password !== confirmPassword;

  return (
    <PageContainer>
      <Text color="#377DFF" fontSize="2xl" fontWeight={500}>
        {isVerificationStep ? 'Verify Your Email' : 'Sign Up'}
      </Text>

      {!isVerificationStep ? (
      <form onSubmit={handleSubmit}>
        <FormControl margin="10px 0" isRequired>
          <FormLabel>Email</FormLabel>
          <Input
            value={email}
            w="300px"
            disabled={isFormDisabled}
            onChange={e => setEmail(e.target.value)}
            placeholder="Email"
            isInvalid={!isEmailEmpty && !isValidEmail(email)}
          />
          <Text
            h="20px"
            color="red"
            fontSize="sm"
            fontWeight={400}
            style={{ visibility: !isEmailEmpty && !isValidEmail(email) ? '' : 'hidden' }}
          >
            {(!isEmailEmpty && !isValidEmail(email) && 'Please Enter A Valid Email') || ' '}
          </Text>
        </FormControl>

        <FormControl margin="10px 0" isRequired>
          <FormLabel>Password</FormLabel>
          <Input
            value={password}
            w="300px"
            type="password"
            disabled={isFormDisabled}
            onChange={e => setPassword(e.target.value)}
            placeholder="Password"
            isInvalid={!isPasswordEmpty && !isPasswordValid}
          />
          <Text
            h="auto"
            color="red"
            fontSize="sm"
            fontWeight={400}
            style={{ visibility: !isPasswordEmpty && !isPasswordValid ? '' : 'hidden' }}
          >
            {!isPasswordEmpty && !isPasswordValid && (
              <Box
                mt={1}
                bg="red.50"
                border="1px solid"
                borderColor="red.200"
                borderRadius="md"
                p={2}
              >
                {passwordValidation.issues.map(issue => (
                  <Text
                    key={issue}
                    fontSize="sm"
                    color="red.600"
                    display="flex"
                    alignItems="center"
                    gap="6px"
                  >
                    <span style={{ fontSize: '10px', color: '#d32f2f' }}>●</span>
                    {issue}
                  </Text>
                ))}
              </Box>
            )}
          </Text>
        </FormControl>

        <FormControl margin="10px 0" isRequired>
          <FormLabel>Confirm Password</FormLabel>
          <Input
            value={confirmPassword}
            w="300px"
            type="password"
            disabled={isFormDisabled}
            onChange={e => setConfirmPassword(e.target.value)}
            placeholder="Confirm Password"
            isInvalid={!isConfirmEmpty && confirmPassword !== password}
          />
          <Text
            h="20px"
            color="red"
            fontSize="sm"
            fontWeight={400}
            style={{ visibility: !isConfirmEmpty && confirmPassword !== password ? '' : 'hidden' }}
          >
            {(!isConfirmEmpty && confirmPassword !== password && 'Passwords do not match') || ' '}
          </Text>
        </FormControl>

        {localError && (
          <Text color="red" fontSize="sm" fontWeight={500} mb={2}>
            {localError}
          </Text>
        )}

        <Button
          margin="10px 0"
          backgroundColor="#007bff"
          color="white"
          _hover={{ bg: '#0069d9', color: 'whitesmoke' }}
          type="submit"
          disabled={isFormDisabled || isButtonDisabled}
        >
          Sign Up
        </Button>
      </form>
      ) : (
      <form onSubmit={handleSubmit}>
        <Text fontSize="sm" color="gray.600" mb={4}>
          Enter the 6-digit code sent to <strong>{email}</strong>
        </Text>

        <FormControl margin="10px 0" isRequired>
          <FormLabel>Verification Code</FormLabel>
          <Input
            value={code}
            w="300px"
            disabled={isFormDisabled}
            onChange={e => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
            placeholder="6-digit code"
            maxLength={6}
          />
        </FormControl>

        {localError && (
          <Text color="red" fontSize="sm" fontWeight={500} mb={2}>
            {localError}
          </Text>
        )}

        <Button
          margin="10px 0"
          backgroundColor="#007bff"
          color="white"
          _hover={{ bg: '#0069d9', color: 'whitesmoke' }}
          type="submit"
          disabled={isFormDisabled || code.length !== 6}
        >
          Verify Email
        </Button>

        <Button
          margin="10px 0 10px 10px"
          variant="outline"
          onClick={() => {
            setIsVerificationStep(false);
            setCode('');
            setLocalError('');
          }}
          disabled={isFormDisabled}
        >
          Back
        </Button>

        <Box mt={4}>
          <Text fontSize="sm" color="gray.600">
            Didn&apos;t receive the code?{' '}
            <Link
              color="#007bff"
              cursor="pointer"
              onClick={handleResend}
              isDisabled={resendDisabled}
              opacity={resendDisabled ? 0.5 : 1}
              pointerEvents={resendDisabled ? 'none' : 'auto'}
            >
              Resend Code
            </Link>
            {resendDisabled && (
              <Text as="span" fontSize="xs" color="gray.500">
                {' '}(wait 30s)
              </Text>
            )}
          </Text>
        </Box>
      </form>
      )}

      {redirect && <Redirect to="/login" />}
    </PageContainer>
  );
}

export default Signup;
